import { useState, useRef, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { useWallet } from "@/context/WalletContext";
import { Wallet, LogOut, ChevronDown, Star, Activity, ArrowUpRight, Copy, Check, Loader2, TrendingUp, Coins, Wallet as WalletIcon, Clock, Settings2 } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { fetchWalletStats, WalletStats, fetchRecentTransactions, RecentTransaction } from "@/lib/walletIntelligence";
import { QRCodeSVG } from "qrcode.react";
import { usePumpPortalLinkedWalletSol } from "@/hooks/usePumpPortalLinkedWalletSol";
import { getPumpPortalTradingWalletPubkey } from "@/lib/pumpPortalConfig";
import { usePumpPortalConfigRevision } from "@/hooks/usePumpPortalConfigRevision";

export function ProfileDropdown() {
  const { connected, publicKey, connect, disconnect } = useWallet();
  const { solPrice, setSidebarMode, setActivitySection, twitterUser, loginWithTwitter, logoutTwitter } = useApp() as any;
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [pumpPortalCopied, setPumpPortalCopied] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const pumpPortalRev = usePumpPortalConfigRevision();
  const pumpPortalPk = useMemo(() => getPumpPortalTradingWalletPubkey(), [pumpPortalRev]);
  const { sol: pumpPortalSol, loading: pumpPortalSolLoading } = usePumpPortalLinkedWalletSol(pumpPortalPk);

  // Calculate dropdown position when opening
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right,
      });
    }
  }, [isOpen]);

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) && !triggerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleCopy = () => {
    if (publicKey) {
      navigator.clipboard.writeText(publicKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleCopyPumpPortal = () => {
    if (pumpPortalPk) {
      navigator.clipboard.writeText(pumpPortalPk);
      setPumpPortalCopied(true);
      setTimeout(() => setPumpPortalCopied(false), 2000);
    }
  };

  const [walletStats, setWalletStats] = useState<WalletStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [recentTransactions, setRecentTransactions] = useState<RecentTransaction[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);

  useEffect(() => {
    if (connected && publicKey) {
      setLoadingStats(true);
      fetchWalletStats(publicKey).then((stats) => {
        if (stats) setWalletStats(stats);
        setLoadingStats(false);
      });
      
      setLoadingTransactions(true);
      fetchRecentTransactions(publicKey, 10).then((txs) => {
        setRecentTransactions(txs);
        setLoadingTransactions(false);
      });
    } else {
      setWalletStats(null);
      setRecentTransactions([]);
    }
  }, [connected, publicKey]);

  const { persistedBotTrades } = useApp() as any;
  const solBalance = walletStats?.solBalance || 0;
  const totalUsd = walletStats?.usdValue || (solBalance * (solPrice || 150));
  const isWhale = walletStats?.isHighValue || totalUsd > 10000;
  
  // Real win rate calculation
  const winRate = useMemo(() => {
    if (!persistedBotTrades || persistedBotTrades.length === 0) return "—";
    const closedTrades = persistedBotTrades.filter((t: any) => t.status === "closed");
    if (closedTrades.length === 0) return "—";
    const wins = closedTrades.filter((t: any) => t.netSol > 0).length;
    return ((wins / closedTrades.length) * 100).toFixed(1);
  }, [persistedBotTrades]);

  const assetLogos = walletStats?.assetLogos || [];
  const tokenCount = walletStats?.tokenCount || 0;
  const txCount = walletStats?.txCount || 0;

  return (
    <div className="relative pointer-events-auto">
      {/* Trigger Button */}
      <button
        ref={triggerRef}
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 rounded-full p-1 pr-3 hover:bg-white/5 transition-colors border border-transparent hover:border-white/10 group"
      >
        <img
          src={twitterUser?.profileImageUrl || (connected ? "https://api.dicebear.com/7.x/avataaars/svg?seed=SolanaWhale" : "https://api.dicebear.com/7.x/avataaars/svg?seed=Guest")}
          alt="Profile"
          className="size-7 rounded-full bg-black/50 ring-1 ring-white/10 group-hover:ring-emerald-400/50 transition-all"
        />
        <div className="hidden md:flex flex-col items-start">
          <span className="text-[10px] font-bold text-white leading-none">
            {twitterUser?.name || (connected ? (isWhale ? "WhaleUser" : "Trader") : "Guest")}
          </span>
          <span className="text-[9px] text-white/50 leading-none mt-0.5">
            {twitterUser?.username ? `@${twitterUser.username}` : (connected && publicKey ? `${publicKey.slice(0, 4)}...${publicKey.slice(-4)}` : "Not Connected")}
          </span>
        </div>
        <ChevronDown className={`size-3 text-white/40 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {/* Dropdown Panel */}
      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          className="fixed w-80 sm:w-96 max-w-[calc(100vw-32px)] max-h-[85vh] overflow-y-auto rounded-2xl bg-[#0b0e11] border border-white/10 shadow-2xl animate-in fade-in zoom-in-95 duration-100 z-[99999] custom-scrollbar"
          style={{ top: `${dropdownPosition.top}px`, right: `${dropdownPosition.right}px` }}
        >
          
          {/* Header */}
          <div className="p-4 border-b border-white/5 bg-gradient-to-b from-emerald-500/10 to-transparent">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <img
                  src={twitterUser?.profileImageUrl || (connected ? "https://api.dicebear.com/7.x/avataaars/svg?seed=SolanaWhale" : "https://api.dicebear.com/7.x/avataaars/svg?seed=Guest")}
                  alt="Profile"
                  className="size-12 rounded-full ring-2 ring-emerald-500/20 bg-black/50"
                />
                <div>
                  <h3 className="text-sm font-bold text-white flex items-center gap-2">
                    {twitterUser?.name || (connected ? (isWhale ? "WhaleUser.sol" : "Solana Wallet") : "Guest Account")}
                    {loadingStats && <Loader2 className="size-3 animate-spin text-emerald-400" />}
                  </h3>
                  {twitterUser ? (
                    <div className="flex items-center gap-1.5 mt-1">
                      <span className="text-xs font-mono text-blue-400/80 bg-blue-500/10 px-1.5 py-0.5 rounded">
                        @{twitterUser.username}
                      </span>
                    </div>
                  ) : connected ? (
                    <div className="flex items-center gap-1.5 mt-1">
                      <span className="text-xs font-mono text-emerald-400/80 bg-emerald-500/10 px-1.5 py-0.5 rounded">
                        {publicKey?.slice(0,4)}...{publicKey?.slice(-4)}
                      </span>
                      <button onClick={handleCopy} className="text-white/40 hover:text-white transition-colors">
                        {copied ? <Check className="size-3 text-emerald-400" /> : <Copy className="size-3" />}
                      </button>
                    </div>
                  ) : (
                    <span className="text-xs text-white/40 mt-1 block">Connect wallet or X to view profile</span>
                  )}
                </div>
              </div>
              {connected && publicKey && (
                <div className="bg-white p-1 rounded-md shrink-0 shadow-[0_0_15px_rgba(16,185,129,0.2)]">
                  <QRCodeSVG value={publicKey} size={48} />
                </div>
              )}
            </div>

            {connected && (
              <div className="mt-4 pt-4 border-t border-white/5">
                <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold mb-1">Total Net Worth</div>
                <div className="text-2xl font-black text-white tracking-tight">${totalUsd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
              </div>
            )}
          </div>

          {connected ? (
            <>
              {/* Wallet Intelligence */}
              <div className="p-4 border-b border-white/5 bg-white/[0.02]">
                <div className="flex items-center gap-1.5 mb-3">
                  <Activity className="size-3.5 text-indigo-400" />
                  <span className="text-[10px] font-black uppercase tracking-widest text-indigo-400">Wallet Intelligence</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-black/40 rounded-lg p-2.5 border border-white/5 hover:border-white/10 transition-colors">
                    <div className="text-[9px] text-white/40 uppercase mb-0.5">Trader Tier</div>
                    <div className="text-xs font-bold text-amber-400 flex items-center gap-1">
                      <Star className="size-3" fill="currentColor" /> {isWhale ? "Whale" : "Shrimp"}
                    </div>
                  </div>
                  <div className="bg-black/40 rounded-lg p-2.5 border border-white/5 hover:border-white/10 transition-colors">
                    <div className="text-[9px] text-white/40 uppercase mb-0.5">Win Rate (All Time)</div>
                    <div className="text-xs font-bold text-emerald-400 flex items-center gap-1">
                      <ArrowUpRight className="size-3" /> {winRate !== "—" ? `${winRate}%` : "—"}
                    </div>
                  </div>
                  <div className="bg-black/40 rounded-lg p-2.5 border border-white/5 hover:border-white/10 transition-colors">
                    <div className="text-[9px] text-white/40 uppercase mb-0.5">Risk Profile</div>
                    <div className="text-xs font-bold text-rose-400">{tokenCount > 30 ? "Degen" : "Safe"}</div>
                  </div>
                  <div className="bg-black/40 rounded-lg p-2.5 border border-white/5 hover:border-white/10 transition-colors">
                    <div className="text-[9px] text-white/40 uppercase mb-0.5">Total Tokens</div>
                    <div className="text-xs font-bold text-white">{tokenCount}</div>
                  </div>
                  <div className="bg-black/40 rounded-lg p-2.5 border border-white/5 hover:border-white/10 transition-colors col-span-2">
                    <div className="text-[9px] text-white/40 uppercase mb-0.5">Transaction Activity</div>
                    <div className="flex items-center justify-between">
                      <div className="text-xs font-bold text-white">{txCount} transactions</div>
                      <div className="flex items-center gap-1 text-[10px] text-emerald-400">
                        <TrendingUp className="size-3" />
                        <span>Active</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Assets List */}
              <div className="p-4 border-b border-white/5">
                 <div className="flex items-center justify-between mb-3">
                   <div className="text-[10px] font-black uppercase tracking-widest text-white/40">Your Assets</div>
                   <div className="flex items-center gap-1 text-[9px] text-white/40">
                     <Coins className="size-3" />
                     <span>{tokenCount} tokens</span>
                   </div>
                 </div>
                 
                 <div className="space-y-3">
                   {/* SOL - Internal Wallet */}
                   <div className="flex items-center justify-between group hover:bg-white/5 p-2 rounded-lg transition-colors">
                     <div className="flex items-center gap-3">
                       <div className="size-8 rounded-full bg-gradient-to-br from-emerald-500/20 to-green-500/20 flex items-center justify-center ring-1 ring-emerald-500/30">
                         <img src="https://cryptologos.cc/logos/solana-sol-logo.svg?v=025" className="size-4" alt="SOL" />
                       </div>
                       <div>
                         <div className="text-xs font-bold text-white flex items-center gap-1">
                           SOL
                           <span className="text-[9px] text-emerald-400/70 font-normal">Internal</span>
                         </div>
                         <div className="text-[10px] text-white/40">Secure Trading Wallet</div>
                       </div>
                     </div>
                     <div className="text-right">
                       <div className="text-sm font-bold text-white">{solBalance.toLocaleString(undefined, { maximumFractionDigits: 4 })}</div>
                       <div className="text-[10px] text-emerald-400">${(solBalance * (solPrice || 150)).toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
                     </div>
                   </div>

                   {/* SOL - PumpPortal Wallet */}
                   {pumpPortalPk && (
                     <div className="flex items-center justify-between group hover:bg-white/5 p-2 rounded-lg transition-colors cursor-pointer" onClick={handleCopyPumpPortal}>
                       <div className="flex items-center gap-3">
                         <div className="size-8 rounded-full bg-gradient-to-br from-emerald-500/20 to-green-500/20 flex items-center justify-center ring-1 ring-emerald-500/30">
                           <img src="https://cryptologos.cc/logos/solana-sol-logo.svg?v=025" className="size-4" alt="SOL" />
                         </div>
                         <div>
                           <div className="text-xs font-bold text-white flex items-center gap-1">
                             SOL
                             <span className="text-[9px] text-emerald-400/70 font-normal">PumpPortal</span>
                           </div>
                           <div className="text-[10px] text-white/40">Trading Wallet</div>
                         </div>
                       </div>
                       <div className="text-right flex items-center gap-2">
                         {pumpPortalSolLoading ? (
                           <Loader2 className="size-3 animate-spin text-white/40" />
                         ) : pumpPortalSol === null ? (
                           <div className="text-[10px] text-rose-400">Error</div>
                         ) : (
                           <>
                             <div className="text-sm font-bold text-white">{pumpPortalSol.toLocaleString(undefined, { maximumFractionDigits: 4 })}</div>
                             <div className="text-[10px] text-emerald-400">${(pumpPortalSol * (solPrice || 150)).toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
                           </>
                         )}
                         {pumpPortalCopied ? (
                           <Check className="size-3 text-emerald-400" />
                         ) : (
                           <Copy className="size-3 text-white/40 group-hover:text-white/60 transition-colors" />
                         )}
                       </div>
                     </div>
                   )}

                   {/* SPL Assets */}
                   {assetLogos.length > 0 && (
                     <>
                       <div className="h-px bg-white/5 my-2" />
                       <div className="space-y-2">
                         {assetLogos.slice(0, 4).map((logo, i) => (
                           <div key={i} className="flex items-center justify-between group hover:bg-white/5 p-2 rounded-lg transition-colors">
                             <div className="flex items-center gap-3">
                               <div className="size-8 rounded-full bg-gradient-to-br from-amber-500/20 to-orange-500/20 flex items-center justify-center ring-1 ring-amber-500/30 overflow-hidden">
                                 <img src={logo} className="size-full object-cover" alt="Token" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                               </div>
                               <div>
                                 <div className="text-xs font-bold text-white">Token #{i + 1}</div>
                                 <div className="text-[10px] text-white/40">SPL Asset</div>
                               </div>
                             </div>
                             <div className="text-right">
                               <div className="text-[10px] text-white/40">View on Solscan</div>
                             </div>
                           </div>
                         ))}
                         {assetLogos.length > 4 && (
                           <div className="text-center py-2">
                             <span className="text-[10px] text-white/40">+{assetLogos.length - 4} more assets</span>
                           </div>
                         )}
                       </div>
                     </>
                   )}

                   {assetLogos.length === 0 && tokenCount === 0 && (
                     <div className="text-center py-4">
                       <WalletIcon className="size-8 text-white/20 mx-auto mb-2" />
                       <div className="text-[10px] text-white/40">No additional assets found</div>
                     </div>
                   )}
                 </div>
              </div>
              
              {/* Recent Transactions */}
              <div className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Activity className="size-3.5 text-emerald-400" />
                    <span className="text-[10px] font-black uppercase tracking-widest text-white/40">Recent Transactions</span>
                  </div>
                  <span className="text-[9px] text-white/40 font-bold">{recentTransactions.length}</span>
                </div>
                
                {loadingTransactions ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="size-4 animate-spin text-white/40" />
                  </div>
                ) : recentTransactions.length === 0 ? (
                  <div className="text-center py-4">
                    <Clock className="size-6 text-white/20 mx-auto mb-2" />
                    <div className="text-[10px] text-white/40">No recent transactions</div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {recentTransactions.slice(0, 10).map((tx) => (
                      <div key={tx.signature} className="flex items-center justify-between p-2 rounded-lg bg-white/[0.02] hover:bg-white/[0.05] border border-white/5 transition-colors">
                        <div className="flex items-center gap-2">
                          <div className={`size-2 rounded-full ${tx.status === 'Success' ? 'bg-emerald-400' : 'bg-rose-400'}`} />
                          <div className="flex flex-col">
                            <span className="text-[10px] font-bold text-white">{tx.type}</span>
                            <span className="text-[8px] text-white/40 font-mono">{tx.signature.slice(0, 8)}...</span>
                          </div>
                        </div>
                        <div className="flex flex-col items-end">
                          {tx.amount && (
                            <span className="text-[10px] font-mono font-bold text-white">{tx.amount.toFixed(4)} SOL</span>
                          )}
                          <span className="text-[8px] text-white/40">
                            {new Date(tx.timestamp).toLocaleTimeString()}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Footer Actions */}
              <div className="p-2 space-y-1">
                {twitterUser ? (
                  <button
                    onClick={() => {
                      logoutTwitter();
                      setIsOpen(false);
                    }}
                    className="w-full flex items-center justify-center gap-2 p-2 rounded-xl text-xs font-bold text-rose-400 hover:bg-rose-500/10 transition-colors"
                  >
                    <LogOut className="size-3.5" /> Disconnect X Account
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      setIsOpen(false);
                      loginWithTwitter();
                    }}
                    className="w-full flex items-center justify-center gap-2 p-2 rounded-xl bg-white/5 text-xs font-bold text-white/90 hover:bg-white/10 hover:text-white transition-all border border-white/10 shadow-[inset_0_1px_0_rgba(255,255,255,0.1)] group/xbtn"
                  >
                    <svg viewBox="0 0 24 24" className="size-3.5 fill-current opacity-80 group-hover/xbtn:opacity-100 transition-opacity">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                    Connect X Account
                  </button>
                )}
                <button
                  onClick={() => {
                    setSidebarMode("analytics");
                    setActivitySection("analytics");
                    setIsOpen(false);
                  }}
                  className="w-full flex items-center justify-center gap-2 p-2 rounded-xl text-xs font-bold text-white/70 hover:bg-white/10 hover:text-white transition-colors"
                >
                  <Settings2 className="size-3.5" /> Setup & Configuration
                </button>
                <button
                  onClick={() => {
                    disconnect();
                    setIsOpen(false);
                  }}
                  className="w-full flex items-center justify-center gap-2 p-2 rounded-xl text-xs font-bold text-rose-400 hover:bg-rose-500/10 transition-colors"
                >
                  <LogOut className="size-3.5" /> Disconnect Wallet
                </button>
              </div>
            </>
          ) : (
            <div className="p-6 flex flex-col items-center justify-center text-center">
              <div className="size-12 rounded-full bg-emerald-500/10 flex items-center justify-center mb-3">
                <Wallet className="size-6 text-emerald-400" />
              </div>
              <h4 className="text-sm font-bold text-white mb-1">Not Initialized</h4>
              <p className="text-xs text-white/50 mb-4 max-w-[200px]">Initialize a secure internal wallet to view assets, balances, and trading intelligence.</p>
              <button
                onClick={() => {
                  setIsOpen(false);
                  loginWithTwitter();
                }}
                className="w-full py-2.5 rounded-xl bg-white/5 text-white/90 text-xs font-bold border border-white/10 hover:bg-white/10 transition-all flex items-center justify-center gap-2 mb-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.1)] group/xbtn"
              >
                <svg viewBox="0 0 24 24" className="size-3.5 fill-current opacity-80 group-hover/xbtn:opacity-100 transition-opacity">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
                Continue with X
              </button>
              <button
                onClick={() => {
                  setIsOpen(false);
                  connect();
                }}
                className="w-full py-2.5 rounded-xl bg-emerald-500/20 text-emerald-400 text-xs font-bold border border-emerald-500/30 hover:bg-emerald-500/30 transition-all mb-2"
              >
                Initialize Wallet
              </button>
              <button
                onClick={() => {
                  setSidebarMode("analytics");
                  setActivitySection("analytics");
                  setIsOpen(false);
                }}
                className="w-full py-2.5 rounded-xl bg-white/5 text-white/70 text-xs font-bold border border-white/10 hover:bg-white/10 hover:text-white transition-all flex items-center justify-center gap-2"
              >
                <Settings2 className="size-3.5" /> Setup & Configuration
              </button>
            </div>
          )}

        </div>, document.body)}
    </div>
  );
}
